"use client";

import React, { useState, useEffect } from "react"; // Import useState and useEffect hooks
import {
  Button,
  Image,
  Link,
  useDisclosure,
  Chip,
  Tooltip,
} from "@heroui/react";
import { IDecklistV2 } from "@/models/decklist";
import DecklistCard from "@/components/Decklists/DecklistCard";
import { getLeaderLife } from "@/lib/leaderManager";
import type { DeckIdentity } from "@/interfaces/DeckIdentity";
import { myDayjs } from "@/lib/dayjsHelper";
import {
  IconCopy,
  IconEdit,
  IconShoppingCart,
  IconStar,
} from "@tabler/icons-react";
import DeckStats, {
  getCardTypes,
  getSearchers,
  getCardList,
} from "@/components/Decklists/DeckStats";
import { getTCGPlayerMassEntryURL } from "@/lib/tcgplayer/tcgplayerAffiliateLink";
import { formatPrice } from "@/lib/moneyHelper";
import { useClientMediaQuery } from "@/lib/hooks/isMobile";
import { abbrevColor } from "@/lib/colorHelper";
import DecklistsNav from "@/components/Decklists/DecklistsNav";
import { getPlacement } from "@/lib/placementHelper";
import { SupportedGames } from "@/types/game";
import { getDeckIdentity } from "@/lib/archetypeManager";
import { getGameHandler } from "@/games";
import { LeaderSquareWithBorder } from "@/components/leaders/LeaderSquareWithBorder";

import GenericCardModalWithNavigation from "@/components/CardModal/GenericCardModalWithNavigation";
import { OPItem, UAItem, RBItem, PalworldItem } from "@/interfaces/CardItem";
import { formatFormat } from "@/lib/formatHelper";
import { setPalworldCards } from "@/lib/games/palworld/getCardDataLocal";
import type { PalworldAaData } from "@/lib/games/palworld/getCardData";
import {
  getFullCardId,
  getSetFullName,
} from "@/lib/games/unionArena/getCardDataLocal";
import { getTCGPlayerProductId } from "@/lib/tcgplayer/getCardPrice";
import { CDN } from "@/lib/cdn";
import { getDeckZones, zoneCardIds } from "@/lib/deckZones";
import ArchetypeSelector from "@/components/Decklists/ArchetypeSelector";

function formatDate(inputDate: string): Date {
  // parse date of format 2025-04-14
  const date = new Date(inputDate);
  return date;
}

export default function Decklist({
  isRec = false,
  deck,
  id,
  price: initialPrice,
  gameName,
  palworldCards,
  palworldAaData,
  archetypeData,
}: {
  isRec?: boolean;
  deck: IDecklistV2;
  id: string;
  price?: number;
  gameName: SupportedGames;
  palworldCards?: PalworldItem[];
  palworldAaData?: PalworldAaData;
  archetypeData?: DeckIdentity;
}) {
  // Declare state variables to store decklists data and colors
  const [nav, setNav] = useState<string>("");
  const [decklist, setDecklist] = useState<IDecklistV2 | null>(deck); // Provide correct type for decklists state variable
  const [copyText, setCopyText] = useState<string>("Copy for Sim");
  const [price, setPrice] = useState<number>(initialPrice || 0);
  const [date, setDate] = useState("");
  const [error, setError] = useState<Error | unknown>(null); // Specify the type of error as 'Error' or 'null'
  const [isLoading, setIsLoading] = useState(true);
  const { isOpen, onOpen, onOpenChange } = useDisclosure();
  const [cardId, setCardId] = useState<string | null>(null);
  const [currentCardIndex, setCurrentCardIndex] = useState<number>(0);
  const [allCardIds, setAllCardIds] = useState<string[]>([]);
  const [searcherCardIds, setSearcherCardIds] = useState<string[]>([]);
  const [isSearcherMode, setIsSearcherMode] = useState(false);
  const gameHandler = getGameHandler(gameName);

  let cardData;
  if (cardId) {
    cardData = gameHandler.getCardData(cardId);
  }

  // Set Palworld cards on client side
  useEffect(() => {
    if (gameName === "palworld" && palworldCards && palworldAaData) {
      setPalworldCards(palworldCards, palworldAaData);
      console.log(
        "[Decklist Client] Set Palworld cards:",
        palworldCards.length,
      );
    }
  }, [gameName, palworldCards, palworldAaData]);

  // Fetch prices at runtime and calculate decklist price
  useEffect(() => {
    const fetchDecklistPrice = async () => {
      if (!decklist?.decklist || initialPrice) return; // Skip if no decklist or price already provided

      try {
        const apiUrl = process.env.NEXT_PUBLIC_API_URL;
        if (!apiUrl) return;

        // Fetch prices from backend
        const response = await fetch(`${apiUrl}/api/prices?game=${gameName}`);
        if (!response.ok) return;

        const prices = await response.json();

        // Calculate decklist price
        let totalPrice = 0;
        const cards = decklist.decklist.split(";");

        for (const card of cards) {
          const [count, cardId] = card.split("x");
          const productId = getTCGPlayerProductId(gameName, cardId);

          if (productId && prices[productId]) {
            const priceData = prices[productId];
            // Use low price if available, otherwise fall back to market price
            const priceString =
              priceData.l && priceData.l.trim() !== ""
                ? priceData.l
                : priceData.m;
            if (priceString && priceString.trim() !== "") {
              const cardPrice = parseFloat(priceString);
              const cardCount = parseInt(count);
              if (
                !isNaN(cardPrice) &&
                !isNaN(cardCount) &&
                cardPrice >= 0 &&
                cardCount > 0
              ) {
                totalPrice += cardCount * cardPrice;
              }
            }
          }
        }

        // Final safety check: ensure totalPrice is valid before setting
        setPrice(!isNaN(totalPrice) && totalPrice >= 0 ? totalPrice : 0);
      } catch (error) {
        console.error("Failed to fetch decklist price:", error);
      }
    };

    fetchDecklistPrice();
  }, [decklist, gameName, initialPrice]);

  // Populate searcherCardIds when decklist changes
  useEffect(() => {
    if (decklist && decklist.decklist && gameName === "one-piece") {
      const cardList = getCardList(decklist.decklist, gameName);
      const searchers = getSearchers(cardList);
      const ids = searchers.map((s) => s.id);
      setSearcherCardIds(ids);
    }
  }, [decklist, gameName]);

  // Populate allCardIds when decklist changes
  useEffect(() => {
    const cardIds: string[] = [];

    if (decklist) {
      // Navigation order follows the rendered zones exactly, so a game that
      // adds a zone (sideboard, extra deck) gets correct modal paging for free.
      const zones = getDeckZones(gameHandler, {
        decklist: decklist.decklist ?? "",
        sideboard: decklist.sideboard,
        identityId: decklist.leader_id,
      });

      // Games whose identity is a real card list it first, unless a zone
      // already includes it (Riftbound's setup zone leads with the Legend).
      const inZones = zones.some((z) =>
        zoneCardIds(z).includes(decklist.leader_id),
      );
      if (gameHandler.hasLeader() && !inZones) {
        cardIds.push(decklist.leader_id);
      }

      for (const zone of zones) {
        cardIds.push(...zoneCardIds(zone));
      }
    }

    setAllCardIds(cardIds);
  }, [decklist, gameHandler, gameName]);

  const handleOpen = (cardId: string) => {
    setIsSearcherMode(false);
    setCardId(cardId);
    // Find the index of the clicked card and set it as current
    const index = allCardIds.findIndex((id) => id === cardId);
    if (index >= 0) {
      setCurrentCardIndex(index);
    }
    onOpen();
  };

  const handleOpenSearcher = (cardId: string) => {
    setIsSearcherMode(true);
    setCardId(cardId);
    // Find the index of the clicked card in searcher list
    const index = searcherCardIds.findIndex((id) => id === cardId);
    if (index >= 0) {
      setCurrentCardIndex(index);
    }
    onOpen();
  };

  const handleNavigate = (direction: "prev" | "next") => {
    const currentList = isSearcherMode ? searcherCardIds : allCardIds;
    if (currentList.length === 0) return;

    let newIndex;
    if (direction === "prev") {
      newIndex =
        currentCardIndex > 0 ? currentCardIndex - 1 : currentList.length - 1;
    } else {
      newIndex =
        currentCardIndex < currentList.length - 1 ? currentCardIndex + 1 : 0;
    }

    setCurrentCardIndex(newIndex);
    setCardId(currentList[newIndex]);
  };

  useEffect(() => {
    if (decklist) {
      if (decklist.timestamp) {
        const timestamp = decklist.timestamp * 1000;
        setDate(myDayjs(timestamp).fromNow());
      } else {
        setDate(myDayjs(formatDate(decklist.date)).fromNow());
      }
    }
  }, [decklist]);

  function copyCards(game: SupportedGames) {
    // handle this for Union Arena

    let cardsStr = "";
    let cardsMap: Record<string, number> = {};
    if (decklist && decklist.decklist && decklist.decklist.length > 0) {
      // Games whose deck identity is a real card include it in the export.
      if (gameHandler.hasLeader()) {
        const leaderId = decklist.leader_id;
        cardsMap[leaderId] = 1;
        cardsStr += `1x${leaderId}\n`;
      }

      for (const card of decklist.decklist.split(";")) {
        const count = parseInt(card.split("x")[0]);
        const cardId = card.split("x")[1];
        let cardNoAA: string = cardId;
        if (cardId.includes("_p")) {
          cardNoAA = cardId.split("_")[0];
        }
        // The handler decides the exported form, and may drop a card entirely
        // (One Piece omits DON). This used to skip anything containing "DON"
        // for every game.
        const exported = gameHandler.getExportCardId
          ? gameHandler.getExportCardId(cardNoAA)
          : cardNoAA;
        if (exported === null) {
          continue;
        }
        cardNoAA = exported;
        cardsStr += `${count}x${cardNoAA}\n`;
        if (!(cardNoAA in cardsMap)) {
          cardsMap[cardNoAA] = count;
        }
      }

      // const sortedEntries = Object.entries(cardsMap).sort(([keyA], [keyB]) =>
      //   keyA.localeCompare(keyB),
      // );
      // let cardsList = sortedEntries.map((value) => {
      //   return `${value[1]}x${value[0]}`;
      // });
      navigator.clipboard.writeText(cardsStr);
      setCopyText("Copied!");
      setTimeout(() => {
        setCopyText("Copy for Sim");
      }, 1000);
    }
  }

  function getDecklist() {
    let cardsStr = "";
    let cardsMap: Record<string, number> = {};
    if (decklist && decklist.decklist && decklist.decklist.length > 0) {
      const leaderId = decklist.leader_id;
      cardsMap[leaderId] = 1;
      cardsStr += `1x${leaderId};`;
      for (const card of decklist.decklist.split(";")) {
        const count = parseInt(card.split("x")[0]);
        const cardId = card.split("x")[1];
        if (card.includes("DON")) {
          continue;
        }
        let cardNoAA: string = cardId;
        if (cardId.includes("_p")) {
          cardNoAA = cardId.split("_")[0];
        }
        cardsStr += `${count}x${cardNoAA};`;
        if (!(cardNoAA in cardsMap)) {
          cardsMap[cardNoAA] = count;
        }
      }
    }
    return cardsStr;
  }

  if (!decklist) {
    return <div>Loading</div>;
  }

  const leaderId = decklist.leader_id;
  // Use archetype data passed from server if available (for Palworld from API)
  // Otherwise derive/lookup on client
  const leaderData: DeckIdentity | undefined =
    archetypeData || getDeckIdentity(gameName, leaderId, decklist.decklist);

  console.log(
    "[TEST] Decklist component - using archetypeData:",
    !!archetypeData,
    "leaderData:",
    leaderData,
  );
  // Leader card
  let cardsDisplay = [];

  let leaderLife: number | undefined = undefined;

  if (gameHandler.hasLeader()) {
    leaderLife = getLeaderLife(leaderId);
    cardsDisplay.push(
      <DecklistCard
        gameName={gameName}
        key={leaderId}
        classNames="w-1/3 mb-2 sm:w-1/6 md:mb-4"
        cardId={leaderId}
        count="1"
        onPress={() => handleOpen(leaderId)}
        showPrice={true}
      />,
    );
  }

  // Rendered zones come from the handler; games that declare none get the
  // shared default (main deck, plus a sideboard when the decklist has one).
  const deckZones = decklist
    ? getDeckZones(gameHandler, {
        decklist: decklist.decklist ?? "",
        sideboard: decklist.sideboard,
        identityId: leaderId,
      })
    : [];
  const setupZone = deckZones.find((z) => z.id === "setup");
  // Only labelled when the deck has more than one zone to distinguish.
  const mainZoneLabel = deckZones.find((z) => z.id === "main")?.label;
  const setupCards = setupZone
    ? zoneCardIds(setupZone).map((cardId) => (
        <DecklistCard
          gameName={gameName}
          key={`setup-${cardId}`}
          classNames="w-1/3 mb-2 sm:w-1/6 md:mb-4"
          cardId={cardId}
          count="1"
          onPress={() => handleOpen(cardId)}
          showPrice={true}
        />
      ))
    : [];

  if (decklist) {
    let cards: string = decklist.decklist || ""; // Handle the case where decklist.decklist is null or undefined
    cards =
      deckZones.find((z) => z.id === "main")?.cards ??
      gameHandler.sortDecklist(cards, decklist.leader_id);
    const cardsSplit: string[] = cards.split(";");
    for (const card of cardsSplit) {
      const [count, cardId] = card.split("x");
      cardsDisplay.push(
        <DecklistCard
          gameName={gameName}
          key={cardId}
          classNames="w-1/3 mb-2 sm:w-1/6 md:mb-4"
          cardId={cardId}
          count={count}
          onPress={() => handleOpen(cardId)}
          showPrice={true}
        />,
      );
    }
  }

  let sideboardDisplay = [];
  if (decklist && decklist.sideboard) {
    let cards: string = decklist.sideboard || ""; // Handle the case where decklist.decklist is null or undefined
    cards = gameHandler.sortDecklist(cards, decklist.leader_id);
    const cardsSplit: string[] = cards.split(";");
    for (const card of cardsSplit) {
      const [count, cardId] = card.split("x");
      sideboardDisplay.push(
        <DecklistCard
          gameName={gameName}
          key={cardId}
          classNames="w-1/3 mb-2 sm:w-1/6 md:mb-4"
          cardId={cardId}
          count={count}
          onPress={() => handleOpen(cardId)}
          showPrice={true}
        />,
      );
    }
  }

  // One Piece was the only game that checked this; the guard is game-agnostic,
  // so every game now gets it rather than offering navigation through a
  // single-card list.
  const canNavigate = isSearcherMode
    ? searcherCardIds.length > 1
    : allCardIds.length > 1;

  return (
    <div>
      {gameName === "one-piece" && (
        <DecklistsNav
          isRec={isRec}
          leaderId={leaderId}
          leaderData={leaderData}
          set={decklist.set}
          region={decklist.region}
          author={decklist.author}
        />
      )}
      <div className="flex flex-row items-center">
        <div>
          <LeaderSquareWithBorder
            leaderId={leaderId}
            size={100}
            borderSize={3}
            colors={leaderData?.color}
            image={leaderData?.image}
            gameName={gameName}
          />
        </div>
        <div className="ml-4 flex flex-col justify-center">
          <h1 className="inline-block text-2xl sm:text-4xl font-semibold text-slate-900 tracking-tight dark:text-slate-100">
            <div className="flex items-center gap-2">
              {isRec && (
                <Tooltip
                  content={`Gumgum.gg recommended decklist for ${decklist.set} ${leaderData?.name}`}
                >
                  <div>
                    <div className="block sm:hidden">
                      <IconStar
                        color={"rgb(245, 165, 36)"}
                        height={24}
                        width={24}
                      />
                    </div>
                    <div className="hidden sm:block">
                      <IconStar
                        color={"rgb(245, 165, 36)"}
                        height={30}
                        width={30}
                      />
                    </div>
                  </div>
                </Tooltip>
              )}{" "}
              {formatFormat(decklist.set)}{" "}
              {leaderData &&
                (leaderData.color.includes("/")
                  ? " " + abbrevColor(leaderData.color, gameName) + ""
                  : "")}{" "}
              {leaderData && getNameWordBreak(leaderData.name)} Decklist
            </div>
          </h1>
          <div className="hidden sm:block">
            <Subtitle decklist={decklist} date={date} />
          </div>
          {/* <div className="dark:text-slate-400">{dayjs(newDate).fromNow()}</div> */}
        </div>
      </div>
      <div className="block sm:hidden">
        <Subtitle decklist={decklist} date={date} />
      </div>

      <h2 className="mt-2 dark:text-slate-400">
        <Description
          gameName={gameName}
          leaderData={leaderData}
          decklist={decklist}
        />
      </h2>

      <EventChips decklist={decklist} />

      {/* Archetype selector for Palworld in dev mode */}
      {gameName === "palworld" && (
        <ArchetypeSelector
          decklistId={decklist.id}
          currentArchetypeId={decklist.leader_id}
          gameId={91}
        />
      )}

      <div className="flex flex-row items-center overflow-scroll no-visible-scroll gap-x-2 gap-y-2 mb-4">
        <DecklistButtons
          game={gameName}
          copyCards={copyCards}
          getDecklist={getDecklist}
          price={price}
          copyText={copyText}
        />
      </div>
      {setupCards.length > 0 && (
        <>
          {setupZone?.label && (
            <div className="text-lg font-semibold mt-8 mb-4">
              {setupZone.label}
            </div>
          )}
          <div className="flex flex-wrap -m-1">{setupCards}</div>
        </>
      )}
      {mainZoneLabel && (
        <div className="text-lg font-semibold mt-8 mb-4">{mainZoneLabel}</div>
      )}
      <div className="flex flex-wrap -m-1">{cardsDisplay}</div>
      {sideboardDisplay.length > 0 && (
        <>
          <div className="text-lg font-semibold mt-8 mb-4">Sideboard</div>
          <div className="flex flex-wrap -m-1">{sideboardDisplay}</div>
        </>
      )}

      {gameName === "one-piece" && decklist.decklist && (
        <div className="mt-8">
          <DeckStats
            leaderLife={leaderLife}
            decklist={decklist.decklist}
            onCardPress={handleOpenSearcher}
          />
        </div>
      )}
      {/*
        One modal for every game. These were four near-identical blocks that
        differed only in a cast, the gameName literal, One Piece's navigation
        guard, and Riftbound's rotation - so a new game silently rendered no
        card modal at all.
      */}
      <GenericCardModalWithNavigation
        isOpen={isOpen}
        onOpenChange={onOpenChange}
        cardData={cardData}
        gameName={gameName}
        aaCardId={cardData?.id}
        onNavigate={canNavigate ? handleNavigate : undefined}
        enableRotation={
          cardData ? gameHandler.isRotatable?.(cardData) : undefined
        }
      />
    </div>
  );
}

function EventChips({ decklist }: { decklist: IDecklistV2 }) {
  let placement = getPlacement(decklist.placement, decklist.placement_text);

  return (
    <div className="flex gap-2 mb-4 mt-4 max-w-full overflow-x-auto whitespace-nowrap">
      {decklist.tournament_type && <Chip>{decklist.tournament_type}</Chip>}
      {decklist.participants !== null && (
        <Chip>{decklist.participants} Players</Chip>
      )}
      <Chip>{placement}</Chip>
      {decklist.score && decklist.score !== "" && <Chip>{decklist.score}</Chip>}
    </div>
  );
}

function formatListWithAnd(items: string[]): string {
  const length = items.length;

  if (length === 0) {
    return ""; // If the list is empty, return an empty string
  }

  if (length === 1) {
    return items[0]; // If there's only one item, return it as is
  }

  if (length === 2) {
    return items.join(" and "); // If there are two items, join them with "and"
  }

  // For 3 or more items, join the list with ", ", adding "and" before the last item
  return items.slice(0, -1).join(", ") + " and " + items[length - 1];
}

function DecklistButtons({
  game,
  copyCards,
  getDecklist,
  price,
  copyText,
}: {
  game: SupportedGames;
  copyCards: (game: SupportedGames) => void;
  getDecklist: () => string;
  price: number;
  copyText: string;
}) {
  const isMobile = useClientMediaQuery("(max-width: 600px)");

  let buttonSize = "md";
  let curCopyText = copyText;
  let editText = "Edit Deck";
  if (isMobile) {
    buttonSize = "sm";
    curCopyText = curCopyText.replace("for Sim", "");
    editText = "Edit";
  }

  return (
    <div className="flex gap-x-2 gap-y-2">
      <Button
        startContent={<IconCopy height={18} width={18} />}
        size={buttonSize as any}
        radius="sm"
        color="primary"
        onPress={() => copyCards(game)}
      >
        {curCopyText}
      </Button>
      {game === "one-piece" && (
        <Link href={`/deckbuilder?deck=${getDecklist()}&layout=edit`}>
          <Button
            size={buttonSize as any}
            startContent={<IconEdit height={18} width={18} />}
            radius="sm"
            color="secondary"
          >
            {editText}
          </Button>
        </Link>
      )}
      {price !== 0 && price && !isNaN(price) && isFinite(price) && (
        <Link
          href={getTCGPlayerMassEntryURL(game, getDecklist(), "decklist")}
          target="_blank"
        >
          <Button
            onPress={() => {
              if (typeof window !== "undefined" && window.gtag) {
                window.gtag("event", "buy_decklist", {
                  value: price,
                });
              }
            }}
            size={buttonSize as any}
            startContent={<IconShoppingCart height={18} width={18} />}
            radius="sm"
            color="danger"
          >
            Buy{" "}
            <Chip size={buttonSize as any} variant="flat">
              ~{formatPrice(price)}
            </Chip>
          </Button>
        </Link>
      )}
    </div>
  );
}

function getNameWordBreak(name: string) {
  // really only an issue with Monkey.D.Luffy but it makes the title look weird on mobile
  if (name === "Monkey.D.Luffy") {
    return "Monkey D. Luffy";
  } else {
    return name;
  }
}

// Country code mapping and helper functions
const countryCodeMap: { [key: string]: string } = {
  USA: "US",
  GBR: "GB",
  CAN: "CA",
  AUS: "AU",
  JPN: "JP",
  JAPAN: "JP",
  CANADA: "CA",
  BRAZIL: "BR",
  SINGAPORE: "SG",
  "SOUTH AFRICA": "ZA",
  FRANCE: "FR",
  PHILIPPINES: "PH",
  MEXICO: "MX",
  GREECE: "GR",
  ITALY: "IT",
  NA: "",
  EU: "",
  OC: "",
  LA: "",
};

const countryNameMap: { [key: string]: string } = {
  US: "United States",
  GB: "United Kingdom",
  CA: "Canada",
  AU: "Australia",
  JP: "Japan",
  BR: "Brazil",
  SG: "Singapore",
  ZA: "South Africa",
  FR: "France",
  PH: "Philippines",
  MX: "Mexico",
  GR: "Greece",
  IT: "Italy",
};

function getNormalizedCountryCode(countryCode: string | undefined): string {
  if (!countryCode) return "";
  let code = countryCode.toUpperCase().trim();
  if (countryCodeMap.hasOwnProperty(code)) {
    code = countryCodeMap[code];
    if (!code) return "";
  }
  if (code.length !== 2) return "";
  return code;
}

function getCountryFlag(countryCode: string): string {
  if (!countryCode || countryCode.length !== 2) return "";
  const codePoints = countryCode
    .split("")
    .map((char) => 127397 + char.charCodeAt(0));
  return String.fromCodePoint(...codePoints);
}

function getCountryName(code: string): string {
  return countryNameMap[code] || code;
}

function getDataProvider(decklist: IDecklistV2) {
  if (decklist.source_provider === "egman") {
    return (
      <>
        <span className="mx-2 flex-shrink-0">·</span>
        <Link href={decklist.src2} className="flex-shrink-0">
          <div className="flex flex-row items-center gap-2">
            <Image
              src={`${CDN}/shared/logo/other/egman_logo.png`}
              width={20}
              className="flex-shrink-0"
            />
            <span>Data by The Egman</span>
          </div>
        </Link>
      </>
    );
  } else {
    return <></>;
  }
}

function Subtitle({ decklist, date }: { decklist: IDecklistV2; date: string }) {
  let curSrc = decklist.src;
  if (decklist.source_provider === "egman") {
    curSrc = decklist.src2;
  }

  const countryCode = getNormalizedCountryCode(decklist.country);
  const flag = countryCode ? getCountryFlag(countryCode) : "";
  const countryName = countryCode ? getCountryName(countryCode) : "";

  return (
    <h2 className="mt-2 dark:text-slate-400">
      <div className="flex flex-row flex-nowrap items-center overflow-x-auto no-visible-scroll gap-1.5">
        {countryCode && (
          <Tooltip
            content={
              <div className="flex items-center gap-1.5">
                {flag && <span>{flag}</span>}
                <span>{countryName}</span>
              </div>
            }
          >
            <div className="flex-shrink-0 px-1 py-0.5 text-[10px] font-semibold border border-slate-600 rounded bg-slate-800/50 text-slate-400 leading-none">
              {countryCode}
            </div>
          </Tooltip>
        )}
        <div className="flex-shrink-0">
          <Link size="md" color="primary" href={curSrc}>
            {decklist.author}
          </Link>
        </div>
        <span className="mx-2 flex-shrink-0">·</span>
        <span className="flex-shrink-0">{date}</span>
        {getDataProvider(decklist)}
      </div>
    </h2>
  );
}

function Description({
  gameName,
  leaderData,
  decklist,
}: {
  gameName: SupportedGames;
  leaderData: DeckIdentity | undefined;
  decklist: IDecklistV2;
}) {
  return (
    <>
      Top placing {formatFormat(decklist.set)} {leaderData?.color}
      {leaderData && leaderData.color.includes("/")
        ? " (" + abbrevColor(leaderData.color, gameName) + ")"
        : ""}{" "}
      {leaderData && leaderData.name}{" "}
      {/* Only show leader ID for games with card-based leaders (not archetypes) */}
      {gameName !== "palworld" && leaderData?.id} decklist featuring{" "}
      {formatListWithAnd(getCardTypes(decklist, gameName))} type cards.
    </>
  );
}
